/**
 * The Sayagie HTTP client. Mirrors HexagieApp
 * `src/shared/services/httpClient.ts`.
 *
 * Responsibilities:
 *   - x-api-key on every request, bearer on everything except the six
 *     bearer-less auth routes.
 *   - 401 -> single-flight refresh -> replay the original request once.
 *   - Gateway errors toast with a de-dupe window, and never log the user out.
 *
 * Runs alongside the legacy `utils/axios.js`, which still points at the old
 * backend. Nothing here touches that.
 */

import axios from "axios";
import ENDPOINTS from "./endpoints";
import { showErrorToast } from "../../components/Toast";
import { API_BASE_URL, API_KEY } from "./env";
import { getApiErrorMessage } from "./apiError";
import { requestTokenRefresh } from "./refreshClient";
import { clearTokens, getAuthToken, getRefreshToken, setTokens } from "./tokenStore";

const CLIENT = axios.create({
  baseURL: API_BASE_URL,
  paramsSerializer: { indexes: null },
});

/**
 * These are x-api-key only. Attaching a bearer is not merely unnecessary, it
 * is harmful: it puts them into the 401-refresh path, where a wrong OTP can
 * trigger a refresh loop.
 */
const BEARERLESS_PATHS = [
  ENDPOINTS.AUTH.sessionStart,
  ENDPOINTS.AUTH.sessionVerify,
  ENDPOINTS.AUTH.sessionDetails,
  ENDPOINTS.AUTH.otpRequest,
  ENDPOINTS.AUTH.otpVerify,
  ENDPOINTS.AUTH.refresh,
  ENDPOINTS.PAYMENTS.walletTopUpCallback,
  ENDPOINTS.ACCOUNT.reactivationInitiate,
  ENDPOINTS.ACCOUNT.reactivationConfirm,
];

const isBearerlessPath = (url) =>
  Boolean(url) && BEARERLESS_PATHS.some((path) => url.includes(path));

const SERVER_DOWN_TITLE = "Server Unavailable";
const SERVER_DOWN_FALLBACK =
  "The server is temporarily unavailable. Please try again.";
const SERVER_DOWN_TOAST_GAP = 4000;

let lastServerDownToastAt = 0;

/**
 * A screen firing several parallel requests against a down backend would
 * otherwise stack identical toasts, so repeats inside the window collapse.
 */
const notifyServerDown = (error) => {
  const now = Date.now();
  if (now - lastServerDownToastAt < SERVER_DOWN_TOAST_GAP) return;

  lastServerDownToastAt = now;

  showErrorToast(
    getApiErrorMessage(error, SERVER_DOWN_FALLBACK),
    SERVER_DOWN_TITLE
  );
};

let refreshPromise = null;

const runRefresh = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const tokens = await requestTokenRefresh(refreshToken);
    if (!tokens?.authToken) return null;

    setTokens({
      authToken: tokens.authToken,
      refreshToken: tokens.refreshToken ?? refreshToken,
    });

    return tokens.authToken;
  } catch {
    return null;
  }
};

/**
 * Single-flight: several requests failing with 401 at once share one refresh
 * rather than racing to mint competing tokens.
 */
export const refreshAuthToken = () => {
  if (!refreshPromise) {
    refreshPromise = runRefresh().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

CLIENT.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    /**
     * Replaced, not merged, exactly as the app's `httpClient` does.
     *
     * `...config.headers` used to come last. At interceptor time that object
     * is axios's own merged defaults, so the spread put
     * `Accept: application/json, text/plain, *\/*` back over the line above
     * it, and left `Content-Type` riding on axios inferring it from the body
     * rather than on what is set here.
     */
    config.headers = {
      Accept: "application/json",
      "Cache-Control": "no-cache",
      "Content-Type": "application/json",
      ...(API_KEY ? { "x-api-key": API_KEY } : {}),
    };

    config.params = config.params || {};

    if (token && !isBearerlessPath(config.url)) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

CLIENT.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const original = error?.config;

    // Every 5xx is the server being down. Toast, but never log out: a 503
    // is an upstream outage, not an expired session.
    if (status >= 500) {
      notifyServerDown(error);
    }

    if (
      status !== 401 ||
      !original ||
      original.__isRetry ||
      isBearerlessPath(original.url)
    ) {
      return Promise.reject(error);
    }

    original.__isRetry = true;

    const authToken = await refreshAuthToken();

    if (authToken) {
      return CLIENT(original);
    }

    clearTokens();
    return Promise.reject(error);
  }
);

export default CLIENT;
